/**
 * Store - Legacy compatibility layer
 * This file maintains backward compatibility with existing code
 * while delegating to the new dataService layer
 */

import { Project, Task, Epic, Story, Sprint, Retrospective, Report, Comment, UserRequest, Notification, AppState } from './types';
import * as dataService from './services/dataService';
import { generateId } from './utils/idGenerator';
import { canDeleteProjectTask } from './utils/projectAccess';
import { normalizeOrganizationId } from './utils/urlToOrg';

///////////////////// Service /////////////////////////////////
export const loadStateFromService = dataService.loadState;
export const saveStateFromService = dataService.saveState;

///// State bridge
let getState: (() => AppState) | null = null;
let setState: ((state: AppState) => void) | null = null;

const getFallbackState = (): AppState => ({
  organizations: [],
  users: [],
  projects: [],
  tasks: [],
  epics: [],
  stories: [],
  sprints: [],
  retrospectives: [],
  reports: [],
  teams: [],
  reminders: [],
  standupNotes: [],
  notifications: [],
  userRequests: [],
  changeLogs: [],
  attachments: [],
  currentUser: null,
  currentOrganization: null,
} as unknown as AppState);

type PersistedCommentEntityType = 'task' | 'epic' | 'story' | 'project' | 'sprint' | 'retrospective' | 'report';

const isPersistedCommentEntityType = (
  entityType: keyof typeof entityMap
): entityType is PersistedCommentEntityType => entityType !== 'organization';

const updateEntityCollectionInState = (
  state: AppState,
  entityType: keyof typeof entityMap,
  entityId: string,
  updater: (item: any) => any
): { nextState: AppState; updated: boolean } => {
  const key = entityMap[entityType];
  let updated = false;

  const nextCollection = (state[key] as any[]).map(item => {
    if (item.id !== entityId) {
      return item;
    }

    updated = true;
    return updater(item);
  });

  return {
    nextState: { ...state, [key]: nextCollection } as AppState,
    updated,
  };
};

export function registerStore(
  getter: () => AppState,
  setter: (state: AppState) => void
) {
  getState = getter;
  setState = setter;
}

/**
 * Returns the current AppState. 
 * Since this is async, all callers MUST use 'await'.
 */
export async function loadState(email?: string): Promise<AppState> {
  if (!getState) {
    if (!email) {
      console.warn("[store] No email provided for service load");
      return getFallbackState(); 
    }
    return dataService.loadState(email);
  }
  return getState();
}

export function saveState(state: AppState) {
  if (!setState) {
    throw new Error("Store not initialized");
  }
  setState(state);
}

// --- Helper for Entity Management ---
type EntityKey = 'organizations' | 'tasks' | 'epics' | 'stories' | 'projects' | 'sprints' | 'retrospectives' | 'reports';

const entityMap: Record<string, EntityKey> = {
  organization: 'organizations',
  task: 'tasks',
  epic: 'epics',
  story: 'stories',
  project: 'projects',
  sprint: 'sprints',
  retrospective: 'retrospectives',
  report: 'reports'
};

// --- CRUD Operations ---

export const addProject = async (project: Project): Promise<void> => {
  const state = await loadState();
  console.log("[store] Adding project: ", project);
  console.log("[store] Current state before adding project: ", state);
  console.trace();
  if (!state.currentOrganization) {
    if (state.organizations.length>0) {
      project.organizationId = state.organizations[0].id; // default to first org
    }
    throw new Error('No current organization set');
  }
  return dataService.addProject(project.organizationId, project);
};

export const updateProject = async (projectId: string, updates: Partial<Project>): Promise<void> => {
  const state = await loadState();
  if (!state.currentOrganization) throw new Error('No current organization set');
  return dataService.updateProject(state.currentOrganization.id, projectId, updates);
};

export const deleteProject = async (projectId: string): Promise<void> => {
  const state = await loadState();
  if (!state.currentOrganization) throw new Error('No current organization set');
  return dataService.deleteProject(state.currentOrganization.id, projectId);
};

export const addTask = async (task: Task): Promise<void> => {
  return dataService.addTask(task);
};

export const updateTask = async (taskId: string, updates: Partial<Task>): Promise<void> => {
  const state = await loadState();
  if (!state.currentOrganization) throw new Error('No current organization set');
  return dataService.updateTask(state.currentOrganization.id, taskId, updates);
};

export const deleteTask = async (taskId: string): Promise<void> => {
  const state = await loadState();
  if (!state.currentOrganization) throw new Error('No current organization set');

  if (state.currentUser) {
    const task = (state.tasks || []).find((item) => item.id === taskId);
    const project = task
      ? (state.projects || []).find((item) => item.id === task.projectId) || null
      : null;

    if (project && !canDeleteProjectTask(state.currentUser, project)) {
      throw new Error('You do not have permission to delete tasks.');
    }

    if (!project && state.currentUser.role === 'external_partner') {
      throw new Error('You do not have permission to delete tasks.');
    }
  }

  return dataService.deleteTask(state.currentOrganization.id, taskId);
};

export const addEpic = async (epic: Epic): Promise<void> => {
  return dataService.addEpic(epic);
};

export const updateEpic = async (epicId: string, updates: Partial<Epic>): Promise<void> => {
  const state = await loadState();
  if (!state.currentOrganization) throw new Error('No current organization set');
  return dataService.updateEpic(state.currentOrganization.id, epicId, updates);
};

export const addStory = async (story: Story): Promise<void> => {
  return dataService.addStory(story);
};

export const updateStory = async (storyId: string, updates: Partial<Story>): Promise<void> => {
  const state = await loadState();
  if (!state.currentOrganization) throw new Error('No current organization set');
  return dataService.updateStory(state.currentOrganization.id, storyId, updates);
};

export const addSprint = async (sprint: Sprint): Promise<void> => {
  const state = await loadState();
  const organizationId =
    normalizeOrganizationId(sprint.organizationId) ||
    normalizeOrganizationId(state?.currentOrganization?.id);

  if (!organizationId) {
    throw new Error('No organization set for sprint');
  }

  return dataService.addSprint({
    ...sprint,
    organizationId,
  });
};

export const updateSprint = async (sprintId: string, updates: Partial<Sprint>): Promise<void> => {
  const state = await loadState();
  if (!state.currentOrganization) throw new Error('No current organization set');
  return dataService.updateSprint(state.currentOrganization.id, sprintId, updates);
};

export const addRetrospective = async (retrospective: Retrospective): Promise<void> => {
  const state = await loadState();
  const organizationId = retrospective.organizationId || state?.currentOrganization?.id;

  if (!organizationId) {
    throw new Error('No organization set for retrospective');
  }

  return dataService.addRetrospective({
    ...retrospective,
    organizationId,
    participantIds: retrospective.participantIds || [],
  });
};

export const addReport = async (report: Report): Promise<void> => {
  return dataService.addReport(report);
};

export const updateReport = async (reportId: string, updates: Partial<Report>): Promise<void> => {
  const state = await loadState();
  if (!state.currentOrganization) throw new Error('No current organization set');
  return dataService.updateReport(state.currentOrganization.id, reportId, updates);
};

// --- Notification Management ---

export const addNotification = async (notification: Notification): Promise<void> => {
  const state = await loadState();

  await dataService.addNotification(notification);

  const notifications = [...(state.notifications || [])];
  const existingIndex = notifications.findIndex(item => item.id === notification.id);

  if (existingIndex >= 0) {
    notifications[existingIndex] = { ...notifications[existingIndex], ...notification };
  } else {
    notifications.push(notification);
  }

  saveState({ ...state, notifications });
};

export const updateNotification = async (
  notificationId: string,
  updates: Partial<Notification>
): Promise<void> => {
  const state = await loadState();

  await dataService.updateNotification(notificationId, updates);

  const notifications = (state.notifications || []).map(notification =>
    notification.id === notificationId
      ? { ...notification, ...updates }
      : notification
  );

  saveState({ ...state, notifications });
};

export const markNotificationAsRead = async (notificationId: string): Promise<void> => {
  const readAt = new Date().toISOString();
  await updateNotification(notificationId, { read: true, readAt });
};

export const markAllNotificationsAsRead = async (userId?: string): Promise<void> => {
  const state = await loadState();
  const targetUserId = userId || state.currentUser?.id;

  if (!targetUserId) {
    return;
  }

  const unreadNotifications = (state.notifications || []).filter(
    notification => notification.userId === targetUserId && !notification.read
  );

  if (!unreadNotifications.length) {
    return;
  }

  const readAt = new Date().toISOString();

  await Promise.all(
    unreadNotifications.map(notification =>
      dataService.updateNotification(notification.id, { read: true, readAt })
    )
  );

  const notifications = (state.notifications || []).map(notification => {
    if (notification.userId !== targetUserId) {
      return notification;
    }

    return {
      ...notification,
      read: true,
      readAt: notification.readAt || readAt,
    };
  });

  saveState({ ...state, notifications });
};

export const deleteNotification = async (notificationId: string): Promise<void> => {
  const state = await loadState();

  await dataService.deleteNotification(notificationId);

  const notifications = (state.notifications || []).filter(
    notification => notification.id !== notificationId
  );

  saveState({ ...state, notifications });
};

// --- Comment Management ---

export const addCommentToEntity = async (
  entityType: keyof typeof entityMap,
  entityId: string,
  comment: Comment
): Promise<void> => {
  const state = await loadState();
  const { nextState, updated } = updateEntityCollectionInState(state, entityType, entityId, item => ({
    ...item,
    comments: [...(item.comments || []), comment],
  }));

  if (!updated) return;

  if (isPersistedCommentEntityType(entityType)) {
    await dataService.addCommentToEntity(entityType, entityId, comment);
  }

  saveState(nextState);
};

export const updateCommentInEntity = async (
  entityType: keyof typeof entityMap,
  entityId: string,
  commentId: string,
  updates: Partial<Comment>
): Promise<void> => {
  const state = await loadState();
  let updatedComment: Partial<Comment> | null = null;

  const updateComments = (comments?: Comment[]): Comment[] => {
    if (!comments) return [];
    return comments.map(c => {
      if (c.id === commentId) {
        updatedComment = { ...updates, edited: true, updatedAt: new Date().toISOString() };
        return { ...c, ...updatedComment };
      }
      return c;
    });
  };

  const { nextState, updated } = updateEntityCollectionInState(state, entityType, entityId, item => ({
    ...item,
    comments: updateComments(item.comments),
  }));

  if (!updated || !updatedComment) return;

  if (isPersistedCommentEntityType(entityType)) {
    await dataService.updateCommentInEntity(entityType, entityId, commentId, updatedComment);
  }

  saveState(nextState);
};

export const deleteCommentFromEntity = async (
  entityType: keyof typeof entityMap,
  entityId: string,
  commentId: string
): Promise<void> => {
  const state = await loadState();
  let updated = false;

  const filterComments = (comments?: Comment[]): Comment[] => {
    if (!comments) return [];
    const filtered = comments.filter(c => c.id !== commentId && c.parentCommentId !== commentId);
    if (filtered.length !== comments.length) updated = true;
    return filtered;
  };

  const { nextState } = updateEntityCollectionInState(state, entityType, entityId, item => ({
    ...item,
    comments: filterComments(item.comments),
  }));

  if (!updated) return;

  if (isPersistedCommentEntityType(entityType)) {
    await dataService.deleteCommentFromEntity(entityType, entityId, commentId);
  }

  saveState(nextState);
};

export const addReactionToComment = async (
  entityType: keyof typeof entityMap,
  entityId: string,
  commentId: string,
  userId: string,
  emoji: string
): Promise<void> => {
  const state = await loadState();
  let reactionUpdates: Partial<Comment> | null = null;

  const updateComments = (comments?: Comment[]): Comment[] => {
    if (!comments) return [];
    return comments.map(comment => {
      if (comment.id === commentId) {
        const reactions = comment.reactions || [];
        const exists = reactions.find(r => r.userId === userId && r.emoji === emoji);
        const nextReactions = exists
          ? reactions.filter(r => !(r.userId === userId && r.emoji === emoji))
          : [...reactions, { userId, emoji }];

        reactionUpdates = { reactions: nextReactions };

        return {
          ...comment,
          reactions: nextReactions,
        };
      }
      return comment;
    });
  };

  const { nextState, updated } = updateEntityCollectionInState(state, entityType, entityId, item => ({
    ...item,
    comments: updateComments(item.comments),
  }));

  if (!updated || !reactionUpdates) return;

  if (isPersistedCommentEntityType(entityType)) {
    await dataService.updateCommentInEntity(entityType, entityId, commentId, reactionUpdates);
  }

  saveState(nextState);
};

// --- User Request Management ---

export const addUserRequest = async (
  entityType: 'task' | 'epic',
  entityId: string,
  request: UserRequest
): Promise<void> => {
  const state = await loadState();
  const key = entityMap[entityType];

  (state[key] as any) = (state[key] as any[]).map(item =>
    item.id === entityId 
      ? { ...item, userRequests: [...(item.userRequests || []), request] } 
      : item
  );
  
  const notification: Notification = {
    id: generateId('notif', entityType, entityId, request.requestedUser),
    organizationId: state.currentOrganization?.id || '',
    userId: request.requestedUser,
    type: 'mention',
    priority: request.priority === 'urgent' ? 'urgent' : request.priority === 'high' ? 'high' : 'medium',
    title: `${request.requestedByName} requested your ${request.requestType}`,
    message: request.message || `${request.requestedByName} needs your input on this ${entityType}`,
    entityType,
    entityId,
    actionUrl: `/app/${entityType}s/${entityId}`,
    read: false,
    createdAt: new Date().toISOString(),
  };
  
  state.notifications.push(notification);
  saveState(state);
};

export const fulfillUserRequest = async (
  entityType: 'task' | 'epic',
  entityId: string,
  requestId: string,
  responseCommentId?: string
): Promise<void> => {
  const state = await loadState();
  const key = entityMap[entityType];
  let requesterId = '';
  let responderName = '';
  
  const updateReq = (req: UserRequest) => {
    if (req.id === requestId) {
      requesterId = req.requestedBy;
      responderName = req.requestedUser;
      return { ...req, status: 'fulfilled' as const, fulfilledAt: new Date().toISOString(), responseCommentId };
    }
    return req;
  };
  
  (state[key] as any) = (state[key] as any[]).map(item =>
    item.id === entityId ? { ...item, userRequests: (item.userRequests || []).map(updateReq) } : item
  );
  
  if (requesterId) {
    state.notifications.push({
      id: generateId('notif', entityType, entityId, requesterId),
      organizationId: state.currentOrganization?.id || '',
      userId: requesterId,
      type: 'comment',
      priority: 'medium',
      title: `${responderName} responded to your request`,
      message: `${responderName} has provided the input you requested`,
      entityType,
      entityId,
      actionUrl: `/app/${entityType}s/${entityId}`,
      read: false,
      createdAt: new Date().toISOString(),
    });
  }
  
  saveState(state);
};

export const dismissUserRequest = async (
  entityType: 'task' | 'epic',
  entityId: string,
  requestId: string
): Promise<void> => {
  const state = await loadState();
  const key = entityMap[entityType];
  
  const updateReq = (req: UserRequest) => 
    req.id === requestId 
      ? { ...req, status: 'dismissed' as const, fulfilledAt: new Date().toISOString() } 
      : req;
  
  (state[key] as any) = (state[key] as any[]).map(item =>
    item.id === entityId ? { ...item, userRequests: (item.userRequests || []).map(updateReq) } : item
  );
  
  saveState(state);
};
